import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  Validators,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  DsButtonV2Component,
  DsCardComponent,
  DsCheckComponent,
  DsInputTextComponent,
} from '@customdesignsystem';
import { stringToRutFormatted } from 'src/app/apv/core/utils/formatters';
import {
  getDateformatMonthYearString,
  getNextMonthDate,
  customRutFormValidator,
  toTitleCase,
} from 'src/app/apv/core/utils/functions';
import { GlobalStorage } from 'src/app/apv/core/services/globals.service';
import { StepperLateralComponent } from '../../components/stepper-lateral/stepper-lateral.component';
import { CONFIRM_LABELS, STEP_FOUR } from '../../constants/confirm.constant';
import { SUBSCRIPTION_URLS } from '../../constants/subscription-routes.constant';
import {
  IAmountSelection,
  IDetailRow,
  IEmployerSelection,
  IRegimeFundSelection,
} from '../../models/subscription.model';
import { FundCatalogService } from '../../services/fund-catalog.service';
import { SubscriptionService } from '../../services/subscription.service';
import { SubscriptionStore } from '../../services/subscription.store';

const CURRENT_STEP_INDEX = 3;

const EDIT_STEP_URLS: Record<number, string> = {
  1: SUBSCRIPTION_URLS.amount,
  2: SUBSCRIPTION_URLS.regimeFund,
  3: SUBSCRIPTION_URLS.employer,
};

@Component({
  selector: 'app-confirm-page',
  templateUrl: './confirm.page.html',
  styleUrl: './confirm.page.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StepperLateralComponent,
    DsButtonV2Component,
    DsCardComponent,
    DsCheckComponent,
    DsInputTextComponent,
  ],
})
export class ConfirmPage implements OnInit {
  readonly stepFour = STEP_FOUR;
  readonly currentStepIndex = CURRENT_STEP_INDEX;

  isSubscriptionAccepted: boolean = false;
  showSpinner: boolean = false;
  executiveRutTouched: boolean = false;

  savingAmount: string = '';
  regimeDetails: IDetailRow[] = [];
  employerDetails: IDetailRow[] = [];
  userDetails: IDetailRow[] = [];

  private readonly currentDate = new Date();
  private readonly nextMonth: Date = getNextMonthDate(this.currentDate);
  private readonly nextSecondMonth: Date = getNextMonthDate(this.nextMonth);

  formGroup = new FormGroup({
    rut: new FormControl('', [Validators.required, customRutFormValidator]),
  });

  constructor(
    private readonly router: Router,
    private readonly globalSvc: GlobalStorage,
    private readonly store: SubscriptionStore,
    private readonly subscriptionSvc: SubscriptionService,
    private readonly fundCatalog: FundCatalogService,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const regimeFund = this.store.regimeFund();

    this.savingAmount = this.buildAmount(this.store.amount());
    this.employerDetails = this.buildEmployerDetails(this.store.employer());
    this.userDetails = this.buildUserDetails();

    this.fundCatalog
      .fundName(regimeFund?.fund)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fundName) => {
        this.regimeDetails = this.buildRegimeDetails(regimeFund, fundName);
      });
  }

  private buildAmount(amount: IAmountSelection | null): string {
    if (!amount?.amount) return '';

    return amount.currency === 'uf'
      ? `UF ${amount.amount}`
      : `$${amount.amount}`;
  }

  private buildRegimeDetails(
    regimeFund: IRegimeFundSelection | null,
    fundName: string
  ): IDetailRow[] {
    return [
      { label: CONFIRM_LABELS.regime, value: regimeFund?.regime ?? '' },
      { label: CONFIRM_LABELS.fund, value: fundName },
      {
        label: CONFIRM_LABELS.firstDiscount,
        value: getDateformatMonthYearString(this.nextMonth),
      },
      {
        label: CONFIRM_LABELS.savingStart,
        value: getDateformatMonthYearString(this.nextSecondMonth),
      },
    ];
  }

  private buildEmployerDetails(employer: IEmployerSelection | null): IDetailRow[] {
    if (!employer) return [];

    return [
      ...employer.details,
      { label: CONFIRM_LABELS.email, value: employer.email },
    ];
  }

  private buildUserDetails(): IDetailRow[] {
    const user = this.globalSvc.userData;

    return [
      {
        label: CONFIRM_LABELS.name,
        value: toTitleCase(`${user?.name ?? ''} ${user?.lastName ?? ''}`.trim()),
      },
      {
        label: CONFIRM_LABELS.rut,
        value: user?.rut ? stringToRutFormatted(user.rut) : '',
      },
      { label: CONFIRM_LABELS.email, value: user?.email ?? '' },
    ];
  }

  get canSubscribe(): boolean {
    return this.isSubscriptionAccepted && this.isRutValid();
  }

  get isExecutiveRutInvalid(): boolean {
    return this.executiveRutTouched && !this.isRutValid();
  }

  get executiveRutValue(): string {
    return String(this.formGroup.get('rut')?.value ?? '');
  }

  onExecutiveRutChange(value: string): void {
    const control = this.formGroup.get('rut');
    const sanitized = this.keepKOnlyAsVerifier(value);

    if (sanitized !== value) {
      control?.setValue(value);
      this.cdr.detectChanges();
    }

    control?.setValue(sanitized);
    this.executiveRutTouched = false;
  }

  private keepKOnlyAsVerifier(value: string): string {
    const body = value.slice(0, -1).replace(/[^0-9]/g, '');
    const verifier = value.slice(-1).toUpperCase();
    return /^[0-9K]$/.test(verifier) ? body + verifier : body;
  }

  onExecutiveRutBlur(): void {
    this.executiveRutTouched = true;
  }

  setAcceptance(accepted: boolean): void {
    this.isSubscriptionAccepted = accepted;
  }

  isRutValid(): boolean {
    return this.formGroup.status === 'VALID' || this.formGroup.value.rut === '';
  }

  editStep(step: number): void {
    this.store.setEditMode(true);

    if (step === 3 && !this.store.hasEmployer()) {
      this.router.navigate([SUBSCRIPTION_URLS.addEmployer]);
      return;
    }

    this.router.navigate([EDIT_STEP_URLS[step]]);
  }

  goToPreviousStep(): void {
    this.router.navigate([
      this.store.hasEmployer()
        ? SUBSCRIPTION_URLS.employer
        : SUBSCRIPTION_URLS.addEmployer,
    ]);
  }

  async submitSubscribtionApv(): Promise<void> {
    if (this.showSpinner || !this.canSubscribe) return;

    this.showSpinner = true;

    try {
      await this.subscriptionSvc.submit(this.formGroup.value.rut ?? '');
      this.router.navigate([SUBSCRIPTION_URLS.success]);
    } catch (error) {
      console.error('Error submitting subscription', error);
      this.router.navigate([SUBSCRIPTION_URLS.failed]);
    }
  }
}
